import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { QRCodeCanvas } from "qrcode.react";
import { Download, XCircle, MapPin, Clock, Calendar, Ticket as TicketIcon, User as UserIcon, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import WalletButtons from "@/components/WalletButtons";

export default function TicketView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/reservations/by-token/${token}`);
        setData({ reservation: r.data, tickets: r.data.tickets });
        return;
      } catch { /* try ticket token next */ }
      try {
        const t = await api.get(`/tickets/by-token/${token}`);
        setData({ tickets: [t.data] });
      } catch {
        setErr("Ticket nicht gefunden");
      }
    })();
  }, [token]);

  const cancelRes = async () => {
    if (!window.confirm("Reservierung wirklich stornieren?")) return;
    await api.post(`/reservations/by-token/${token}/cancel`);
    toast.success("Storniert");
    setData((d) => ({ ...d, tickets: (d?.tickets || []).map((t) => ({ ...t, status: "storniert" })) }));
  };

  if (err) return <div className="min-h-screen flex items-center justify-center bg-black text-[#EE9CA1]">{err}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-black text-[#A8A398]">Lade …</div>;

  return (
    <div className="min-h-screen bg-black text-[#F5F0E6] pb-20">
      {/* Foyer-Backdrop */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06] mix-blend-overlay"
        style={{ backgroundImage: "radial-gradient(#D4AF37 1px, transparent 1px)", backgroundSize: "3px 3px" }} />

      <div className="relative max-w-4xl mx-auto p-6 md:p-10">
        <Link to="/program" className="text-xs uppercase tracking-widest text-[#A8A398] hover:text-[#D4AF37]">← Zurück zum Programm</Link>

        {/* Hero-Header */}
        <div className="text-center mt-8">
          <div className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-[#D4AF37]" style={{ textShadow: "0 0 40px rgba(212,175,55,0.35)" }}>
            THE MOVIE HOUSE
          </div>
          <div className="text-[10px] mt-2 uppercase tracking-[0.4em] text-[#A8A398]">More Than Watching Movies.</div>
          <div className="mt-6 inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]/90">
            <span className="w-8 h-px bg-[#D4AF37]/60" /> {data.tickets.length === 1 ? "Dein Ticket" : `Deine Tickets · ${data.tickets.length}`} <span className="w-8 h-px bg-[#D4AF37]/60" />
          </div>
        </div>

        {/* Tickets */}
        <div className="mt-10 space-y-6">
          {data.tickets.map((t) => <TicketStub key={t.id} t={t} />)}
        </div>

        {data.reservation && data.tickets.some((t) => t.status !== "storniert") && (
          <div className="mt-10 text-center">
            <button onClick={cancelRes} className="inline-flex items-center gap-2 text-xs px-5 py-3 border border-[#5B1319] rounded-full hover:bg-[#5B1319]/30 text-[#EE9CA1] uppercase tracking-[0.32em]" data-testid="cancel-reservation-btn">
              <XCircle size={12} /> Reservierung stornieren
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function TicketStub({ t }) {
  const cancelled = t.status === "storniert";
  const qrUrl = `${window.location.origin}/ticket/${t.qr_token}`;
  const poster = t.poster_url || "";
  return (
    <div className={`relative rounded-xl overflow-hidden bg-[#0F0F0F] shadow-2xl ${cancelled ? "opacity-60" : ""}`} data-testid={`ticket-${t.id}`}>
      {/* Blur-Backdrop mit Poster hinter der ganzen Karte */}
      {poster && (
        <>
          <img src={poster} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }}
            className="absolute inset-0 w-full h-full object-cover opacity-25 scale-110" style={{ filter: "blur(24px) saturate(1.1)" }} />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/85" />
        </>
      )}

      {/* Doppelrahmen */}
      <div className="absolute inset-0 rounded-xl border border-[#8B7328]/50 pointer-events-none" />
      <div className="absolute inset-[6px] rounded-lg border border-[#D4AF37]/90 pointer-events-none" />

      {/* Marquee-Bulbs */}
      <div className="absolute inset-x-6 top-3 flex justify-between pointer-events-none">
        {Array.from({ length: 26 }).map((_, i) => (
          <span key={i} className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] shadow-[0_0_6px_rgba(212,175,55,0.9)]" />
        ))}
      </div>
      <div className="absolute inset-x-6 bottom-3 flex justify-between pointer-events-none">
        {Array.from({ length: 26 }).map((_, i) => (
          <span key={i} className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] shadow-[0_0_6px_rgba(212,175,55,0.9)]" />
        ))}
      </div>

      <div className={`grid grid-cols-1 ${poster ? "md:grid-cols-[180px_auto_1fr_auto_260px]" : "md:grid-cols-[1fr_auto_260px]"} relative`}>
        {/* POSTER-SPALTE (links) */}
        {poster && (
          <div className="relative flex items-center justify-center p-6 md:p-8">
            <div className="relative">
              <img src={poster} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="w-40 md:w-44 aspect-[2/3] object-cover rounded shadow-[0_0_40px_rgba(212,175,55,0.35)] border-2 border-[#D4AF37]/60" />
            </div>
          </div>
        )}

        {/* Sub-Perforation zwischen Poster und Haupt-Sektion */}
        {poster && (
          <div className="hidden md:flex flex-col items-center justify-center px-1">
            <div className="h-3/4 w-px border-l border-dashed border-[#D4AF37]/30" />
          </div>
        )}

        {/* HAUPT-SEKTION */}
        <div className="p-8 md:p-10 relative">
          {/* Mini-Logo */}
          <div className="inline-block border border-[#D4AF37] px-4 py-1.5 bg-black/40 backdrop-blur">
            <div className="font-heading text-lg text-[#D4AF37] leading-none tracking-wide">THE MOVIE HOUSE</div>
            <div className="text-[8px] uppercase tracking-[0.35em] text-[#A8A398] mt-0.5 text-center">More · Than · Watching · Movies</div>
          </div>

          <div className="mt-5 flex items-center justify-between border-t border-[#D4AF37]/40 pt-4">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Admit One</div>
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#A8A398]">{t.ticket_type || "Standard"}</div>
          </div>

          <div className="font-heading text-3xl md:text-4xl mt-4 leading-tight break-words">{t.movie_title}</div>

          <div className="mt-6 grid grid-cols-3 gap-5">
            <Field icon={<Calendar size={12} />} label="Datum" v={fmtDate(t.date)} />
            <Field icon={<Clock size={12} />} label="Einlass" v={t.entry_time} />
            <Field icon={<Clock size={12} />} label="Filmstart" v={t.start_time} gold />
          </div>

          <div className="border-t border-[#1A1A1A] mt-6 pt-4 grid grid-cols-2 gap-5">
            <Field icon={<UserIcon size={12} />} label="Gast" v={t.guest_name || "Gast"} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] flex items-center gap-1"><TicketIcon size={12} /> Preis</div>
              <div className="font-heading text-2xl text-[#D4AF37] mt-1">{Number(t.price || 0).toFixed(2)} €</div>
            </div>
          </div>

          <div className="mt-6 text-[10px] uppercase tracking-[0.32em] text-[#575148]">
            Bitte QR-Code am Einlass vorzeigen. Ticket ist personalisiert und nicht übertragbar.
          </div>
        </div>

        {/* Perforation */}
        <div className="hidden md:flex flex-col items-center justify-center px-1 relative">
          <div className="absolute top-0 h-8 w-8 rounded-full bg-black -translate-y-1/2" />
          <div className="absolute bottom-0 h-8 w-8 rounded-full bg-black translate-y-1/2" />
          <div className="h-full w-px border-l-2 border-dashed border-[#D4AF37]/60 my-4" />
        </div>

        {/* STUB */}
        <div className="p-8 md:p-10 bg-[#0A0A0A]/85 backdrop-blur-sm md:border-l-0 border-t md:border-t-0 border-dashed border-[#D4AF37]/60 flex flex-col items-center text-center">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Einlass · Stub</div>
          <div className="h-px w-16 bg-[#D4AF37]/50 mx-auto my-4" />

          <div className="text-[9px] uppercase tracking-[0.4em] text-[#A8A398]">Saal</div>
          <div className="font-heading text-2xl mt-1 flex items-center gap-2">
            <MapPin size={16} className="text-[#D4AF37]" /> {t.hall || "Saal 1"}
          </div>

          <div className="grid grid-cols-2 gap-6 mt-6 w-full">
            <div>
              <div className="text-[9px] uppercase tracking-[0.4em] text-[#A8A398]">Reihe</div>
              <div className="font-heading text-4xl text-[#D4AF37] mt-1 tabular-nums">{t.row}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-[0.4em] text-[#A8A398]">Platz</div>
              <div className="font-heading text-4xl text-[#D4AF37] mt-1 tabular-nums">{t.seat}</div>
            </div>
          </div>

          <div className="mt-6 bg-[#F5F0E6] p-2.5 rounded">
            <QRCodeCanvas value={qrUrl} size={140} bgColor="#F5F0E6" fgColor="#0A0A0A" />
          </div>

          <div className="mt-4 font-mono text-[11px] text-[#D4AF37] tracking-widest">{t.ticket_number}</div>
          <div className="text-[9px] uppercase tracking-[0.32em] text-[#575148] mt-1">
            Status: <span className={cancelled ? "text-[#EE9CA1]" : "text-[#D4AF37]"}>{t.status}</span>
          </div>
          <div className="mt-4 flex gap-3 items-center">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tickets/${t.id}/pdf`} target="_blank" rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.32em] px-4 py-2 border border-[#D4AF37]/60 text-[#D4AF37] rounded-full hover:bg-[#D4AF37]/10">
              <Download size={12} /> PDF
            </a>
          </div>
          <WalletButtons ticketId={t.id} />
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tickets/${t.id}/ics`}
            data-testid={`ticket-ical-${t.id}`}
            title="Zum Kalender hinzufügen (iPhone, Google, Outlook)"
            className="inline-flex items-center gap-2 px-3 py-2 rounded border border-[#8FBC8F]/50 text-[#8FBC8F] text-[10px] uppercase tracking-widest hover:bg-[#8FBC8F]/10 transitions-safe">
            <CalendarPlus size={12} /> Zum Kalender
          </a>
        </div>
      </div>

      {cancelled && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="rotate-[-12deg] px-8 py-3 border-4 border-[#7A1921] text-[#EE9CA1] font-heading text-4xl bg-black/50 backdrop-blur uppercase tracking-widest">
            Storniert
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ icon, label, v, gold }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] flex items-center gap-1">{icon} {label}</div>
      <div className={`font-mono text-xl mt-1 ${gold ? "text-[#D4AF37] font-heading" : ""}`}>{v}</div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
    return d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
}
